// EditReceiptDialog - Edit an existing receipt
// Pre-fills form with existing data, allows changing all fields including file
// Includes preview of current and new files

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Fullscreen as FullscreenIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { receiptsAPI } from '../../services/api';
import type { Receipt } from '../../types';
import dayjs from 'dayjs';
import ViewReceiptDialog from './ViewReceiptDialog';

interface EditReceiptDialogProps {
  open: boolean;
  submissionId: string;
  receipt: Receipt;
  programCategories: string[];
  onClose: () => void;
  onReceiptUpdated: (receipt: Receipt) => void;
}

const EditReceiptDialog: React.FC<EditReceiptDialogProps> = ({
  open,
  submissionId,
  receipt,
  programCategories,
  onClose,
  onReceiptUpdated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Form state
  const { showSuccess, showError } = useToast();
  const [vendor, setVendor] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newFilePreviewUrl, setNewFilePreviewUrl] = useState<string | null>(null);

  // UI state
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewExpanded, setPreviewExpanded] = useState(!isMobile);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);
  const [viewCurrentReceipt, setViewCurrentReceipt] = useState(false);

  // Update preview expansion based on screen size
  useEffect(() => {
    setPreviewExpanded(!isMobile);
  }, [isMobile]);

  // Pre-fill form when dialog opens or receipt changes
  useEffect(() => {
    if (receipt && open) {
      setVendor(receipt.vendor);
      setReceiptDate(dayjs(receipt.receiptDate).format('YYYY-MM-DD'));
      setCategory(receipt.expenseCategory);
      setAmount(receipt.amount.toString());
      setNotes(receipt.notes || '');
      setNewFile(null);
      setNewFilePreviewUrl(null);
      setError(null);
      setPreviewExpanded(!isMobile);
    }
  }, [receipt, open, isMobile]);

  // ========================================
  // FILE PREVIEW HANDLING
  // ========================================

  const isImage = (file: File) => {
    return file.type.startsWith('image/');
  };

  const isPdf = (file: File) => {
    return file.type === 'application/pdf';
  };

  const isCurrentFileImage = () => {
    const ext = receipt.originalFileName?.split('.').pop()?.toLowerCase();
    return receipt.mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext || '');
  };

  const isCurrentFilePdf = () => {
    const ext = receipt.originalFileName?.split('.').pop()?.toLowerCase();
    return receipt.mimeType === 'application/pdf' || ext === 'pdf';
  };

  useEffect(() => {
    // Clean up previous preview URL
    if (newFilePreviewUrl) {
      URL.revokeObjectURL(newFilePreviewUrl);
    }

    // Create new preview URL for images
    if (newFile && isImage(newFile)) {
      const objectUrl = URL.createObjectURL(newFile);
      setNewFilePreviewUrl(objectUrl);
    } else {
      setNewFilePreviewUrl(null);
    }

    // Cleanup on unmount
    return () => {
      if (newFilePreviewUrl) {
        URL.revokeObjectURL(newFilePreviewUrl);
      }
    };
  }, [newFile]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log('📎 File selected:', file.name);
      setNewFile(file);
      // Auto-expand preview when file is selected
      setPreviewExpanded(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!vendor.trim() || !category || !amount) {
      setError('Please fill in all required fields');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // If user uploaded a new file, handle it
      let blobName = receipt.blobName;
      let originalFileName = receipt.originalFileName;
      let fileSize = receipt.fileSize;
      let mimeType = receipt.mimeType;

      console.log('📁 Original file:', { blobName, originalFileName });
      console.log('📁 New file selected:', newFile?.name);

      if (newFile) {
        console.log('📤 Uploading new file...');
        setUploading(true);
        const uploadResult = await receiptsAPI.uploadFile(submissionId, newFile);
        blobName = uploadResult.blobName;
        originalFileName = uploadResult.originalFileName;
        fileSize = uploadResult.fileSize;
        mimeType = uploadResult.mimeType;
        setUploading(false);
        console.log('✅ Upload result:', { blobName, originalFileName });
      } else {
        console.log('ℹ️ No new file - keeping existing file');
      }

      const updateData = {
        vendor: vendor.trim(),
        receiptDate,
        expenseCategory: category,
        amount: amountNum,
        notes: notes.trim() || undefined,
        blobName,
        originalFileName,
        fileSize,
        mimeType,
      };

      console.log('📤 Sending to API:', updateData);

      // Update receipt
      const updatedReceipt = await receiptsAPI.updateReceipt(
        submissionId,
        receipt.receiptId,
        updateData
      );

      console.log('✅ API returned:', updatedReceipt);

      // Success!
      showSuccess('Receipt updated successfully!');
      onReceiptUpdated(updatedReceipt);
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to update receipt';
      showError(errorMessage);
      setError(errorMessage);
      console.error('❌ Error updating receipt:', err);
      console.error('Response:', err.response?.data);
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  // ========================================
  // RENDER PREVIEW SECTION
  // ========================================

  const renderPreview = () => {
    // If new file selected, show new file preview
    if (newFile) {
      return (
        <Box sx={{ mb: 2 }}>
          {/* Preview Header */}
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle2" color="success.main" fontWeight="bold">
              New Receipt Preview
            </Typography>
            <Box>
              {isMobile && (
                <IconButton
                  size="small"
                  onClick={() => setPreviewExpanded(!previewExpanded)}
                >
                  {previewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Preview Content */}
          <Collapse in={previewExpanded}>
            <Box
              sx={{
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'grey.100',
              }}
            >
              {/* Image Preview */}
              {isImage(newFile) && newFilePreviewUrl && (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={newFilePreviewUrl}
                    alt="New receipt preview"
                    sx={{
                      width: '100%',
                      maxHeight: 400,
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.8)',
                      },
                    }}
                    onClick={() => setFullscreenPreview(true)}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </Box>
              )}

              {/* PDF Preview */}
              {isPdf(newFile) && (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <PdfIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {newFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    PDF files cannot be previewed during upload
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    You'll be able to view it after saving
                  </Typography>
                </Box>
              )}

              {/* Other File Types */}
              {!isImage(newFile) && !isPdf(newFile) && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {newFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Preview not available for this file type
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      );
    }

    // Otherwise show current file info with view option
    return (
      <Box sx={{ mb: 2 }}>
        {/* Preview Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Current Receipt
          </Typography>
          <Box>
            {isMobile && (
              <IconButton
                size="small"
                onClick={() => setPreviewExpanded(!previewExpanded)}
              >
                {previewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Current File Info */}
        <Collapse in={previewExpanded}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              p: 2,
              bgcolor: 'grey.50',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                {isCurrentFilePdf() ? (
                  <PdfIcon color="error" />
                ) : isCurrentFileImage() ? (
                  <FileIcon color="primary" />
                ) : (
                  <FileIcon color="action" />
                )}
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {receipt.originalFileName || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Current receipt file
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => setViewCurrentReceipt(true)}
              >
                View
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Upload a new file below to replace this receipt
            </Typography>
          </Box>
        </Collapse>
      </Box>
    );
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Receipt</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              {/* Preview Section */}
              <Grid item xs={12}>
                {renderPreview()}
              </Grid>

              {/* File Upload - Optional Replacement */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: newFile ? 'success.main' : 'grey.300',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: newFile ? 'success.50' : 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                    },
                  }}
                  onClick={() => document.getElementById('edit-file-upload')?.click()}
                >
                  <input
                    id="edit-file-upload"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  
                  {newFile ? (
                    <>
                      <CheckIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="body1" color="success.main">
                        New file: {newFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to change file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        Click to upload new receipt (optional)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Leave blank to keep existing file
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Vendor */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vendor"
                  placeholder="e.g., Staples, Best Buy"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  required
                />
              </Grid>

              {/* Receipt Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Receipt Date"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              {/* Amount */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                  }}
                  inputProps={{
                    step: '0.01',
                    min: '0.01',
                  }}
                  required
                />
              </Grid>

              {/* Category */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Expense Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                >
                  {programCategories.length > 0 ? (
                    programCategories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No categories available
                    </MenuItem>
                  )}
                </TextField>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes (optional)"
                  placeholder="Add any additional details..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>

              {/* Error Display */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || uploading}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {uploading ? 'Uploading...' : submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Fullscreen Preview Dialog for New File */}
      {newFile && isImage(newFile) && newFilePreviewUrl && (
        <Dialog
          open={fullscreenPreview}
          onClose={() => setFullscreenPreview(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">New Receipt Preview</Typography>
              <IconButton onClick={() => setFullscreenPreview(false)}>
                <ExpandLessIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'grey.900',
                minHeight: '70vh',
              }}
            >
              <Box
                component="img"
                src={newFilePreviewUrl}
                alt="New receipt preview fullscreen"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFullscreenPreview(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* View Current Receipt Dialog */}
      <ViewReceiptDialog
        open={viewCurrentReceipt}
        receipt={receipt}
        onClose={() => setViewCurrentReceipt(false)}
      />
    </>
  );
};

export default EditReceiptDialog;